// The customer's first charging session, and the welcome e-mail that announces
// it (CWA-117). Kept out of onboardingStates.ts because none of this is derived
// from the prospect's stage — it comes from the charging record itself.

import { FirstChargeEmailAttempt, Prospect } from "../types/onboarding";

/**
 * Whether the customer's station has actually charged.
 *
 * Deliberately keyed on the charging record rather than on the stage. The
 * welcome e-mail opens by telling the customer their station has completed its
 * first session, and a stage can be advanced by hand without any session having
 * happened — the charging record is the only real evidence. The backend applies
 * no check of its own, so this is what stands between an agent and a false
 * statement to a customer.
 */
export const hasFirstCharge = (prospect: Prospect): boolean =>
  !!prospect.first_cdr_id;

/**
 * The most recent dispatch attempt, or null when the e-mail has never been
 * tried. Sorted rather than assuming the backend's order, so "most recent"
 * stays true if that order ever changes.
 */
export const lastFirstChargeAttempt = (
  prospect: Prospect
): FirstChargeEmailAttempt | null => {
  const attempts = prospect.first_charge_email_attempts ?? [];
  if (!attempts.length) return null;

  return attempts.reduce((latest, attempt) => {
    if (!attempt.sent_at) return latest;
    if (!latest.sent_at) return attempt;
    return new Date(attempt.sent_at).getTime() >
      new Date(latest.sent_at).getTime()
      ? attempt
      : latest;
  });
};

/**
 * True when the customer has charged but has not been told — no attempt was
 * ever made, or the last one failed. Drives a visible cue rather than leaving
 * an agent to read the history and work it out.
 */
export const firstChargeEmailNeedsAttention = (prospect: Prospect): boolean => {
  if (!hasFirstCharge(prospect)) return false;
  const last = lastFirstChargeAttempt(prospect);
  return !last || last.outcome === "FAILED";
};

// "Sent automatically" / "Sent by ops" — the raw source values never reach the
// screen. Unknown values fall back to the neutral phrasing rather than printing
// an identifier.
export const attemptSourceLabel = (source: string): string => {
  switch (source) {
    case "AUTOMATED":
      return "sent automatically";
    case "ADMIN":
      return "sent by ops";
    default:
      return "sent";
  }
};

export const attemptOutcomeLabel = (outcome: string): string =>
  outcome === "SUCCESS" ? "Sent" : "Failed";

export const attemptOutcomeSeverity = (
  outcome: string
): "success" | "danger" => (outcome === "SUCCESS" ? "success" : "danger");
